import { Box, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import Modal from 'shared/lib/components/modals/modal';
import { UiContext } from 'shared/lib/contexts/uiContext';
import React, { useContext, useState } from 'react';
import ButtonsContainer from 'shared/lib/components/controls/buttons/buttonsContainer';
import SecondaryButton from 'shared/lib/components/controls/buttons/secondaryButton';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';
import UploadArea, {
  UploadedReadedFile,
} from 'shared/lib/components/controls/uploadArea/uploadArea';
import { useTranslation } from 'react-i18next';
import IconFolder from 'shared/icons/iconFolder';
import ItemSizeCell from 'shared/lib/extendedComponents/grids/components/itemSizeCell';

export default observer(function ReplaceFileModal({
  onSubmit,
  handleClose,
  isOpen,
}: {
  onSubmit: (uploadedFile: UploadedReadedFile) => Promise<void>;
  handleClose: () => void;
  isOpen: boolean;
}) {
  const { t } = useTranslation();
  const {
    spacings: { sp },
  } = useContext(UiContext);
  const theme = useTheme();

  const [uploadedFile, setUploadedFile] = useState<UploadedReadedFile | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);

  return (
    <Modal isOpen={isOpen} handleClose={handleClose}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: sp.s60 }}>
        <Typography variant="f24">{t('actions.fileReplacing')}</Typography>

        {uploadedFile ? (
          <Box sx={{ display: 'flex', gap: sp.s40 }}>
            <Typography
              variant="f20b"
              sx={{
                lineClamp: 2,
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              }}
            >
              {uploadedFile.originalFile.name}
            </Typography>
            <ItemSizeCell size={uploadedFile.originalFile.size}></ItemSizeCell>
          </Box>
        ) : (
          <UploadArea
            isDataUrl
            handleAddFile={(file) => setUploadedFile(file)}
            accept={{}}
            text={t('noCategory.uploadFileHelp')}
            buttonText={t('actions.fileUpload')}
            icon={
              <IconFolder
                sizeKey="48x48"
                extColor={theme.palette.text.secondary}
              />
            }
          ></UploadArea>
        )}

        <ButtonsContainer>
          <SecondaryButton onClick={handleClose}>
            {t('common.cancel')}
          </SecondaryButton>
          <PrimaryButton
            loading={isLoading}
            disabled={uploadedFile === null}
            onClick={async () => {
              if (uploadedFile) {
                setIsLoading(true);

                try {
                  await onSubmit(uploadedFile);
                } catch (e) {
                  console.error(e);
                }

                setIsLoading(false);
              }
            }}
          >
            {t('common.save')}
          </PrimaryButton>
        </ButtonsContainer>
      </Box>
    </Modal>
  );
});
